import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Workflow,
  MousePointer,
  Zap,
  GitBranch,
  RefreshCw,
  Clock,
  Check,
  BrainCircuit,
  Eye,
  Mic,
  Database,
  Shield,
  Gauge,
  Play,
  History,
  Bug,
  Users,
  Layers,
  ArrowRight,
} from "lucide-react";
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute('/features/workflow-engine')({
  component: WorkflowEnginePage,
});

const features = [
  {
    iconName: "MousePointer",
    title: "Visual Canvas Editor",
    description: "Build workflows with an intuitive drag-and-drop canvas powered by React Flow. See your automation logic visually.",
  },
  {
    iconName: "GitBranch",
    title: "Advanced Logic",
    description: "Conditional branching, routers, parallel execution, and merge nodes for complex business logic.",
  },
  {
    iconName: "RefreshCw",
    title: "Loops & Batch Processing",
    description: "Process collections with loops and handle large datasets with batch start/end nodes.",
  },
  {
    iconName: "Zap",
    title: "Multiple Triggers",
    description: "Start workflows from webhooks, schedules, events, database changes, or manual triggers.",
  },
  {
    iconName: "Workflow",
    title: "Sub-Workflows",
    description: "Embed workflows within workflows. Build modular, reusable automation components.",
  },
  {
    iconName: "Shield",
    title: "Enterprise Controls",
    description: "Rate limiting, retry policies, approval gates, and error handling built-in.",
  },
];

const nodeCategories = [
  {
    category: "Core Flow",
    description: "Essential building blocks",
    nodes: [
      { name: "Input", desc: "Start point" },
      { name: "Output", desc: "End point" },
      { name: "Condition", desc: "If/else logic" },
      { name: "Router", desc: "Multi-path routing" },
      { name: "Loop", desc: "Iterate collections" },
      { name: "Parallel", desc: "Concurrent execution" },
      { name: "Merge", desc: "Combine branches" },
      { name: "Delay", desc: "Wait/pause" },
    ],
  },
  {
    category: "AI & LLM",
    description: "Intelligence nodes",
    nodes: [
      { name: "LLM", desc: "Language models" },
      { name: "Agent", desc: "AI agent invocation" },
      { name: "Orchestrator", desc: "Multi-agent coordination" },
      { name: "RAG Query", desc: "Vector search" },
      { name: "Vision", desc: "Image analysis" },
      { name: "Transcribe", desc: "Audio to text" },
      { name: "Evaluate", desc: "LLM judge" },
    ],
  },
  {
    category: "Memory & Data",
    description: "State management",
    nodes: [
      { name: "Memory Read", desc: "Retrieve context" },
      { name: "Memory Write", desc: "Store context" },
      { name: "Retriever", desc: "Knowledge fetch" },
      { name: "Transform", desc: "Data mapping" },
      { name: "Validator", desc: "Schema validation" },
    ],
  },
  {
    category: "Triggers",
    description: "Workflow initiators",
    nodes: [
      { name: "Webhook", desc: "HTTP trigger" },
      { name: "Schedule", desc: "Cron/interval" },
      { name: "Event Trigger", desc: "DB/file/queue events" },
    ],
  },
  {
    category: "Tools & Actions",
    description: "External operations",
    nodes: [
      { name: "Tool", desc: "MCP tool call" },
      { name: "Tool Executor", desc: "Batch tool execution" },
      { name: "Workflow", desc: "Sub-workflow call" },
    ],
  },
  {
    category: "Enterprise",
    description: "Production controls",
    nodes: [
      { name: "Approval", desc: "Human-in-the-loop" },
      { name: "Rate Limit", desc: "Throttle requests" },
      { name: "Retry Policy", desc: "Failure recovery" },
      { name: "Batch Start/End", desc: "Bulk processing" },
      { name: "Error Handler", desc: "Exception handling" },
    ],
  },
];

const advancedFeatures = [
  {
    icon: History,
    title: "Version History",
    description: "Every change is tracked. Compare versions, restore previous states, and maintain full audit trails.",
  },
  {
    icon: Bug,
    title: "Debug Mode",
    description: "Step through execution, inspect data at each node, preview outputs, and replay past executions.",
  },
  {
    icon: Play,
    title: "Preview Execution",
    description: "Test workflows with sample data before deploying. See exactly how data flows through your automation.",
  },
  {
    icon: Layers,
    title: "Environment Support",
    description: "Separate configurations for development, staging, and production. Deploy with confidence.",
  },
];

const faqItems = [
  {
    question: "Do I need to code to use the workflow builder?",
    answer: "No, most workflows can be built entirely with drag-and-drop. For advanced cases, you can add custom code nodes with JavaScript or Python, but it's optional.",
  },
  {
    question: "How many nodes are available?",
    answer: "We offer 34+ node types across 6 categories: Core Flow, AI & LLM, Memory & Data, Triggers, Tools & Actions, and Enterprise controls. More nodes are added regularly.",
  },
  {
    question: "Can I version control my workflows?",
    answer: "Yes, all workflows are versioned automatically. You can view history, compare versions, restore previous states, and export as JSON for backup or migration.",
  },
  {
    question: "How do I debug a workflow?",
    answer: "Use our built-in debug mode to step through execution, inspect data at each node, and see variable states. You can also replay past executions to diagnose issues.",
  },
  {
    question: "Can workflows call other workflows?",
    answer: "Yes! The Workflow node lets you embed sub-workflows, enabling modular design and reusable automation components.",
  },
  {
    question: "What AI capabilities are built-in?",
    answer: "AI nodes include LLM calls, agent orchestration, RAG queries, vision (image analysis), transcription (audio to text), and evaluation (LLM judge for testing).",
  },
];

function WorkflowEnginePage() {
  return (
    <div>
      <ContentHeader
        title="Visual Workflow Builder"
        description="Build powerful automations with 34+ node types. Connect APIs, orchestrate AI agents, and automate complex business processes—all with a visual drag-and-drop interface."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Features", href: "/features" },
          { label: "Workflow Builder" },
        ]}
        badge="Feature"
      />

      {/* Key Features */}
      <ContentSection
        id="features"
        title="Builder Features"
        background="muted"
      >
        <FeatureShowcase features={features} columns={3} variant="cards" />
      </ContentSection>

      {/* Node Types - Comprehensive */}
      <ContentSection
        id="nodes"
        title="34+ Node Types"
        description="Everything you need to build any automation—from simple integrations to complex AI pipelines."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {nodeCategories.map((category) => (
            <div key={category.category} className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold text-lg">{category.category}</h3>
              <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
              <div className="mt-4 space-y-2">
                {category.nodes.map((node) => (
                  <div key={node.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-primary" />
                      <span>{node.name}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{node.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Button variant="outline" asChild>
            <Link to="/agents/create/canvas">
              Open Canvas Builder
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </ContentSection>

      {/* Advanced Features */}
      <ContentSection
        id="advanced"
        title="Built for Production"
        description="Enterprise-grade features for teams building mission-critical automations."
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {advancedFeatures.map((feature) => (
            <div key={feature.title} className="flex gap-4 rounded-xl border bg-card p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 shrink-0">
                <feature.icon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">{feature.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* How It Works */}
      <ContentSection
        id="how-it-works"
        title="How It Works"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground text-lg font-bold">
              1
            </div>
            <h3 className="mt-4 font-semibold">Add Trigger</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Choose what starts your workflow—webhook, schedule, event, or manual.
            </p>
          </div>
          <div className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground text-lg font-bold">
              2
            </div>
            <h3 className="mt-4 font-semibold">Build Logic</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Drag nodes onto the canvas and connect them to define your automation flow.
            </p>
          </div>
          <div className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground text-lg font-bold">
              3
            </div>
            <h3 className="mt-4 font-semibold">Test & Debug</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Run in debug mode, step through execution, and verify each node works correctly.
            </p>
          </div>
          <div className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground text-lg font-bold">
              4
            </div>
            <h3 className="mt-4 font-semibold">Deploy</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Activate your workflow across environments and monitor executions in real-time.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* All Capabilities */}
      <ContentSection id="capabilities" title="Full Capabilities" background="muted">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            "Visual drag-and-drop canvas",
            "34+ node types",
            "Version control & history",
            "Debug mode with step-through",
            "Execution replay",
            "Data preview at each step",
            "Environment management",
            "Sub-workflow embedding",
            "Parallel execution",
            "Error handling & retries",
            "Rate limiting",
            "Human-in-the-loop approvals",
            "Batch processing",
            "AI/LLM integration",
            "RAG & vector search",
            "Vision & transcription",
            "Template library",
            "JSON export/import",
          ].map((capability) => (
            <div key={capability} className="flex items-center gap-3">
              <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
              <span className="text-sm">{capability}</span>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Build Your First Workflow"
        description="Start automating in minutes with our visual workflow builder. 34+ node types, debug mode, and enterprise controls included."
        primaryAction={{ label: "Open Canvas Builder", href: "/agents/create/canvas" }}
        secondaryAction={{ label: "View All Features", href: "/features" }}
        background="primary"
      />
    </div>
  );
}
